'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge, Button, Skeleton } from '@repo/ui/components';
import { ExternalLink, LinkIcon, Shield, ShieldAlert, ShieldCheck, ShieldMinus, ShieldQuestion, ShieldX, TriangleAlert } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import type { ComponentProps, ReactNode } from 'react';
import Link from 'next/link';
export type CompanyRiskBannerProps = {
  className?: string;
  creditAvailableLabel?: string;
  detailsHref?: string;
  message?: ReactNode;
  onCreateSetupFormLink?: () => void;
  paymentTermsLabel?: string;
  setupFormLinkError?: string;
  setupFormLinkStatus?: 'creating' | 'idle';
  status: CompanyRiskBannerStatus;
  validUntilLabel?: string;
};
export type CompanyRiskBannerStatus = 'credit-limit-exceeded' | 'high' | 'low' | 'medium' | 'not-verified' | 'overdue-payment' | 'unverified-new-company' | 'very-high' | 'very-low';
type BadgeVariant = ComponentProps<typeof Badge>['variant'];
type StatusConfig = {
  badgeClassName?: string;
  badgeVariant: BadgeVariant;
  icon: typeof Shield;
  label: string;
};
const statusConfig: Record<CompanyRiskBannerStatus, StatusConfig> = {
  'credit-limit-exceeded': {
    badgeVariant: 'destructive',
    icon: TriangleAlert,
    label: 'Credit limit exceeded'
  },
  high: {
    badgeClassName: 'bg-orange-100 text-orange-600',
    badgeVariant: 'secondary',
    icon: ShieldAlert,
    label: 'High risk'
  },
  low: {
    badgeClassName: 'bg-blue-500 text-white',
    badgeVariant: 'default',
    icon: Shield,
    label: 'Low risk'
  },
  medium: {
    badgeVariant: 'outline',
    icon: ShieldMinus,
    label: 'Medium risk'
  },
  'not-verified': {
    badgeVariant: 'destructive',
    icon: ShieldQuestion,
    label: 'Not verified'
  },
  'overdue-payment': {
    badgeVariant: 'destructive',
    icon: TriangleAlert,
    label: 'Overdue payment'
  },
  'unverified-new-company': {
    badgeVariant: 'destructive',
    icon: TriangleAlert,
    label: 'Unverified new company'
  },
  'very-high': {
    badgeVariant: 'destructive',
    icon: ShieldX,
    label: 'Very high risk'
  },
  'very-low': {
    badgeClassName: 'bg-blue-500 text-white',
    badgeVariant: 'default',
    icon: ShieldCheck,
    label: 'Very low risk'
  }
};
const statusesWithMoreInfo = new Set<CompanyRiskBannerStatus>(['credit-limit-exceeded', 'high', 'low', 'medium', 'overdue-payment', 'very-high', 'very-low']);
const getDefaultMessage = ({
  canCreateCustomerSetupLink,
  creditAvailableLabel,
  paymentTermsLabel,
  status,
  validUntilLabel
}: {
  canCreateCustomerSetupLink: boolean;
  creditAvailableLabel: CompanyRiskBannerProps['creditAvailableLabel'];
  paymentTermsLabel: CompanyRiskBannerProps['paymentTermsLabel'];
  status: CompanyRiskBannerProps['status'];
  validUntilLabel: CompanyRiskBannerProps['validUntilLabel'];
}) => {
  if (status === 'unverified-new-company') {
    if (!canCreateCustomerSetupLink) return 'This company is not verified yet.';
    return <>
        This company is not verified yet. To start the verification process, send the Customer
        Set-up Form to the client using the dedicated link below.
      </>;
  }
  if (status === 'not-verified') {
    return <>
        This company is not verified yet.
        <br aria-hidden />
        {paymentTermsLabel ?? '-'}
      </>;
  }
  if (status === 'overdue-payment' || status === 'credit-limit-exceeded') {
    return paymentTermsLabel ?? '-';
  }
  return <>
      {validUntilLabel ? `valid until ${validUntilLabel}.` : 'valid until -.'}
      {creditAvailableLabel ? <>
          <br aria-hidden />
          Credit limit available: {creditAvailableLabel}
        </> : null}
      <br aria-hidden />
      {paymentTermsLabel ?? '-'}
    </>;
};
export const CompanyRiskBanner = t0 => {
  const $ = _c(38);
  const {
    className,
    creditAvailableLabel,
    detailsHref,
    message,
    onCreateSetupFormLink,
    paymentTermsLabel,
    setupFormLinkError,
    setupFormLinkStatus: t1,
    status,
    validUntilLabel
  } = t0;
  const setupFormLinkStatus = t1 === undefined ? "idle" : t1;
  const config = statusConfig[status];
  const Icon = config.icon;
  let t2;
  if ($[0] !== detailsHref || $[1] !== status) {
    t2 = detailsHref && statusesWithMoreInfo.has(status);
    $[0] = detailsHref;
    $[1] = status;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const shouldShowMoreInfo = t2;
  const canCreateCustomerSetupLink = status === "unverified-new-company" && Boolean(onCreateSetupFormLink);
  let setupFormLinkActionLabel = "Create customer setup link";
  if (setupFormLinkError) {
    setupFormLinkActionLabel = "Try again";
  }
  if (setupFormLinkStatus === "creating") {
    setupFormLinkActionLabel = "Creating setup link\u2026";
  }
  const t3 = (status === "credit-limit-exceeded" || status === "overdue-payment") && "border-destructive";
  const t4 = status !== "credit-limit-exceeded" && status !== "overdue-payment" && "border-input";
  let t5;
  if ($[3] !== className || $[4] !== t3 || $[5] !== t4) {
    t5 = cn("flex w-full flex-col items-center justify-center gap-4 rounded-sm border bg-primary-foreground px-8 py-4 text-center", t3, t4, className);
    $[3] = className;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== Icon) {
    t6 = <Icon data-icon="inline-start" data-sentry-element="Icon" data-sentry-source-file="company-risk-banner.tsx" />;
    $[7] = Icon;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== config.badgeClassName || $[10] !== config.badgeVariant || $[11] !== config.label || $[12] !== t6) {
    t7 = <Badge className={config.badgeClassName} variant={config.badgeVariant} data-sentry-element="Badge" data-sentry-source-file="company-risk-banner.tsx">{t6}{config.label}</Badge>;
    $[9] = config.badgeClassName;
    $[10] = config.badgeVariant;
    $[11] = config.label;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== canCreateCustomerSetupLink || $[15] !== creditAvailableLabel || $[16] !== message || $[17] !== paymentTermsLabel || $[18] !== status || $[19] !== validUntilLabel) {
    t8 = message ?? getDefaultMessage({
      canCreateCustomerSetupLink,
      creditAvailableLabel,
      paymentTermsLabel,
      status,
      validUntilLabel
    });
    $[14] = canCreateCustomerSetupLink;
    $[15] = creditAvailableLabel;
    $[16] = message;
    $[17] = paymentTermsLabel;
    $[18] = status;
    $[19] = validUntilLabel;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t8) {
    t9 = <p className="text-sm leading-5 text-foreground">{t8}</p>;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== canCreateCustomerSetupLink || $[24] !== onCreateSetupFormLink || $[25] !== setupFormLinkActionLabel || $[26] !== setupFormLinkError || $[27] !== setupFormLinkStatus) {
    t10 = canCreateCustomerSetupLink ? <div className="flex flex-col items-center gap-2"><Button disabled={setupFormLinkStatus !== "idle"} onClick={onCreateSetupFormLink} size="sm" type="button"><LinkIcon data-icon="inline-start" />{setupFormLinkActionLabel}</Button>{setupFormLinkError ? <p className="max-w-md text-sm text-destructive" role="alert">{setupFormLinkError}</p> : null}</div> : null;
    $[23] = canCreateCustomerSetupLink;
    $[24] = onCreateSetupFormLink;
    $[25] = setupFormLinkActionLabel;
    $[26] = setupFormLinkError;
    $[27] = setupFormLinkStatus;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== detailsHref || $[30] !== shouldShowMoreInfo) {
    t11 = shouldShowMoreInfo ? <Button nativeButton={false} render={<Link href={detailsHref} rel="noreferrer" target="_blank" />} size="xs" variant="secondary">More info<ExternalLink data-icon="inline-end" /></Button> : null;
    $[29] = detailsHref;
    $[30] = shouldShowMoreInfo;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t10 || $[33] !== t11 || $[34] !== t5 || $[35] !== t7 || $[36] !== t9) {
    t12 = <section className={t5} data-sentry-component="CompanyRiskBanner" data-sentry-source-file="company-risk-banner.tsx">{t7}{t9}{t10}{t11}</section>;
    $[32] = t10;
    $[33] = t11;
    $[34] = t5;
    $[35] = t7;
    $[36] = t9;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  return t12;
};
export const CompanyRiskBannerSkeleton = t0 => {
  const $ = _c(7);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex w-full flex-col items-center justify-center gap-4 rounded-sm border border-input bg-primary-foreground px-8 py-4", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Skeleton className="h-5 w-36 rounded-4xl" data-sentry-element="Skeleton" data-sentry-source-file="company-risk-banner.tsx" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex w-full flex-col items-center gap-2"><Skeleton className="h-4 w-4/5" data-sentry-element="Skeleton" data-sentry-source-file="company-risk-banner.tsx" /><Skeleton className="h-4 w-3/5" data-sentry-element="Skeleton" data-sentry-source-file="company-risk-banner.tsx" /></div>;
    t4 = <Skeleton className="h-6 w-24" data-sentry-element="Skeleton" data-sentry-source-file="company-risk-banner.tsx" />;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1) {
    t5 = <section className={t1} data-sentry-component="CompanyRiskBannerSkeleton" data-sentry-source-file="company-risk-banner.tsx">{t2}{t3}{t4}</section>;
    $[5] = t1;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};